/**
 * Standardized Analytics Categories and Actions for Record App
 *
 * This file provides type-safe, standardized categories and actions for analytics events.
 * Following these standards ensures:
 * - Consistent naming across the application
 * - Type safety and IDE autocomplete
 * - Easier analytics query writing
 * - Validation of event structure
 */

// ============================================================================
// CORE CATEGORY ENUMS
// ============================================================================

export enum AnalyticsCategory {
	// User navigation and movement
	NAVIGATION = "navigation",

	// UI interactions (buttons, forms, modals)
	UI = "ui",

	// Content creation and management
	CONTENT = "content",

	// Feature-specific usage tracking
	FEATURE_USAGE = "feature_usage",

	// System events, errors, performance
	SYSTEM = "system",

	// User behavior and engagement patterns
	USER_BEHAVIOR = "user_behavior",

	// Record-specific categories
	PROJECT_MANAGEMENT = "project_management",
	FORM_BUILDER = "form_builder",
	DATA_COLLECTION = "data_collection",
	REPORTING = "reporting",
	COLLABORATION = "collaboration",
}

// ============================================================================
// CORE ACTION ENUMS BY CATEGORY
// ============================================================================

export enum NavigationAction {
	PAGE_VIEW = "page_view",
	ROUTE_CHANGE = "route_change",
	REFRESH = "refresh",
	MENU_CLICK = "menu_click",
	BREADCRUMB_CLICK = "breadcrumb_click",
	BACK_BUTTON = "back_button",
	SEARCH_NAVIGATE = "search_navigate",
}

export enum UIAction {
	BUTTON_CLICK = "button_click",
	FORM_SUBMIT = "form_submit",
	FORM_START = "form_start",
	MODAL_OPEN = "modal_open",
	MODAL_CLOSE = "modal_close",
	DROPDOWN_SELECT = "dropdown_select",
	TAB_SWITCH = "tab_switch",
	CHECKBOX_TOGGLE = "checkbox_toggle",
	INPUT_FOCUS = "input_focus",
	VALIDATION_ERROR = "validation_error",
}

export enum ContentAction {
	CREATE = "create",
	READ = "read",
	UPDATE = "update",
	DELETE = "delete",
	DUPLICATE = "duplicate",
	EXPORT = "export",
	IMPORT = "import",
	FILE_UPLOAD = "file_upload",
	FILE_DOWNLOAD = "file_download",
	SAVE_DRAFT = "save_draft",
	PUBLISH = "publish",
}

export enum FeatureAction {
	FEATURE_START = "feature_start",
	FEATURE_COMPLETE = "feature_complete",
	FEATURE_ABANDON = "feature_abandon",
	WORKFLOW_START = "workflow_start",
	WORKFLOW_STEP = "workflow_step",
	WORKFLOW_COMPLETE = "workflow_complete",
	TOOL_SELECT = "tool_select",
	SETTING_CHANGE = "setting_change",
}

export enum SystemAction {
	ERROR = "error",
	NETWORK_ERROR = "network_error",
	VALIDATION_ERROR = "validation_error",
	PERMISSION_ERROR = "permission_error",
	SYNC_START = "sync_start",
	SYNC_COMPLETE = "sync_complete",
	SYNC_ERROR = "sync_error",
	PERFORMANCE_METRIC = "performance_metric",
	SESSION_START = "session_start",
	SESSION_END = "session_end",
}

export enum UserBehaviorAction {
	SESSION_START = "session_start",
	SESSION_HEARTBEAT = "session_heartbeat",
	USER_ACTIVE = "user_active",
	SESSION_END = "session_end",
	IDLE_START = "idle_start",
	IDLE_END = "idle_end",
	SCROLL = "scroll",
	PREFERENCE_CHANGE = "preference_change",
	HELP_VIEW = "help_view",
	TUTORIAL_START = "tutorial_start",
	TUTORIAL_COMPLETE = "tutorial_complete",
	HISTORY_REVERT = "history_revert",
}

// Record-specific actions
export enum ProjectAction {
	PROJECT_CREATE = "project_create",
	PROJECT_OPEN = "project_open",
	PROJECT_EDIT = "project_edit",
	PROJECT_DELETE = "project_delete",
	PROJECT_SHARE = "project_share",
	PROJECT_ARCHIVE = "project_archive",
	PROJECT_RESTORE = "project_restore",
	TEAM_INVITE = "team_invite",
	TEAM_REMOVE = "team_remove",
}

export enum FormBuilderAction {
	TEMPLATE_CREATE = "template_create",
	TEMPLATE_EDIT = "template_edit",
	TEMPLATE_DELETE = "template_delete",
	FIELD_ADD = "field_add",
	FIELD_REMOVE = "field_remove",
	FIELD_REORDER = "field_reorder",
	VALIDATION_ADD = "validation_add",
	PREVIEW_MODE = "preview_mode",
	PUBLISH_TEMPLATE = "publish_template",
}

export enum DataCollectionAction {
	RECORD_START = "record_start",
	RECORD_SAVE = "record_save",
	RECORD_SUBMIT = "record_submit",
	FIELD_UPDATE = "field_update",
	PHOTO_CAPTURE = "photo_capture",
	GPS_CAPTURE = "gps_capture",
	OFFLINE_MODE = "offline_mode",
	SYNC_RECORDS = "sync_records",
}

export enum ReportingAction {
	REPORT_GENERATE = "report_generate",
	REPORT_EXPORT = "report_export",
	REPORT_SCHEDULE = "report_schedule",
	CHART_CREATE = "chart_create",
	FILTER_APPLY = "filter_apply",
	DATA_QUERY = "data_query",
}

// ============================================================================
// FLEXIBLE TYPES - Use enums OR custom strings
// ============================================================================

// Category can be enum OR custom string (escape hatch for new categories)
export type EventCategory = AnalyticsCategory | (string & {});

// Action can be from appropriate enum OR custom string
export type EventAction =
	| NavigationAction
	| UIAction
	| ContentAction
	| FeatureAction
	| SystemAction
	| UserBehaviorAction
	| ProjectAction
	| FormBuilderAction
	| DataCollectionAction
	| ReportingAction
	| (string & {}); // Custom actions allowed

// ============================================================================
// EVENT INTERFACES
// ============================================================================

export interface StandardizedEventInput {
	category: EventCategory;
	action: EventAction;
	label?: string;
	value?: number;
	metadata?: Record<string, any>;
}

// Backwards compatibility with existing simplified interface
export interface CreateEventInput {
	action: EventAction;
	category: EventCategory;
	label?: string;
	value?: number;
	metadata?: Record<string, any>;
}

// ============================================================================
// METADATA TYPE DEFINITIONS
// ============================================================================

export interface BaseMetadata {
	// Common fields for all events
	source?: string; // Where the action originated
	method?: string; // How the action was performed
	success?: boolean; // Whether action succeeded
	duration?: number; // Time taken (ms)
	errorType?: string; // Error classification
	errorMessage?: string; // Error description
}

export interface NavigationMetadata extends BaseMetadata {
	route?: string;
	previousRoute?: string;
	navigationMethod?: "menu" | "breadcrumb" | "direct" | "back" | "forward";
	loadTime?: number;
	referrer?: string;
}

export interface ContentMetadata extends BaseMetadata {
	contentType?: string; // project, form, template, etc.
	contentId?: string; // Unique identifier
	contentName?: string; // Human readable name
	templateId?: string; // Template used
	size?: number; // File size in bytes
	fieldCount?: number; // Number of fields/items
	validationErrors?: number;
}

export interface FeatureMetadata extends BaseMetadata {
	featureName?: string;
	isFirstUse?: boolean;
	stepNumber?: number;
	totalSteps?: number;
	completionRate?: number;
}

export interface SystemMetadata extends BaseMetadata {
	errorCode?: string;
	component?: string;
	stackTrace?: string; // Truncated stack trace
	userAgent?: string; // Truncated user agent
	networkType?: "wifi" | "cellular" | "ethernet" | "unknown";
	connectionSpeed?: "slow-2g" | "2g" | "3g" | "4g" | "unknown";
}

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

export function isStandardCategory(category: string): category is AnalyticsCategory {
	return Object.values(AnalyticsCategory).includes(category as AnalyticsCategory);
}

export function isStandardAction(action: string): boolean {
	const allActions = [
		...Object.values(NavigationAction),
		...Object.values(UIAction),
		...Object.values(ContentAction),
		...Object.values(FeatureAction),
		...Object.values(SystemAction),
		...Object.values(UserBehaviorAction),
		...Object.values(ProjectAction),
		...Object.values(FormBuilderAction),
		...Object.values(DataCollectionAction),
		...Object.values(ReportingAction),
	];
	return allActions.includes(action as any);
}

export function validateEvent(event: CreateEventInput): {
	isValid: boolean;
	warnings: string[];
	errors: string[];
} {
	const warnings: string[] = [];
	const errors: string[] = [];

	// Required fields
	if (!event.action) {
		errors.push("Action is required");
	}
	if (!event.category) {
		errors.push("Category is required");
	}

	// Check if using standard values
	if (event.category && !isStandardCategory(event.category)) {
		warnings.push(`Non-standard category: "${event.category}". Consider using predefined categories.`);
	}

	if (event.action && !isStandardAction(event.action)) {
		warnings.push(`Non-standard action: "${event.action}". Consider using predefined actions.`);
	}

	// Metadata validation
	if (event.metadata) {
		const metadataSize = JSON.stringify(event.metadata).length;
		if (metadataSize > 2048) {
			// 2KB limit
			errors.push(`Metadata too large: ${metadataSize} bytes (limit: 2048)`);
		}

		// Check for sensitive data patterns
		const sensitivePatterns = ["password", "ssn", "email", "phone", "address"];
		const metadataString = JSON.stringify(event.metadata).toLowerCase();
		sensitivePatterns.forEach((pattern) => {
			if (metadataString.includes(pattern)) {
				warnings.push(`Potential sensitive data detected: "${pattern}"`);
			}
		});
	}

	return {
		isValid: errors.length === 0,
		warnings,
		errors,
	};
}

// ============================================================================
// TYPE-SAFE HELPER FUNCTIONS
// ============================================================================

export function createNavigationEvent(action: NavigationAction, metadata?: NavigationMetadata): CreateEventInput {
	return {
		category: AnalyticsCategory.NAVIGATION,
		action,
		metadata,
	};
}

export function createUIEvent(action: UIAction, metadata?: BaseMetadata): CreateEventInput {
	return {
		category: AnalyticsCategory.UI,
		action,
		metadata,
	};
}

export function createContentEvent(action: ContentAction, metadata?: ContentMetadata): CreateEventInput {
	return {
		category: AnalyticsCategory.CONTENT,
		action,
		metadata,
	};
}

export function createFeatureEvent(action: FeatureAction, metadata?: FeatureMetadata): CreateEventInput {
	return {
		category: AnalyticsCategory.FEATURE_USAGE,
		action,
		metadata,
	};
}

export function createSystemEvent(action: SystemAction, metadata?: SystemMetadata): CreateEventInput {
	return {
		category: AnalyticsCategory.SYSTEM,
		action,
		metadata,
	};
}

export function createProjectEvent(action: ProjectAction, metadata?: ContentMetadata): CreateEventInput {
	return {
		category: AnalyticsCategory.PROJECT_MANAGEMENT,
		action,
		metadata,
	};
}

export function createFormBuilderEvent(action: FormBuilderAction, metadata?: ContentMetadata): CreateEventInput {
	return {
		category: AnalyticsCategory.FORM_BUILDER,
		action,
		metadata,
	};
}

export function createDataCollectionEvent(action: DataCollectionAction, metadata?: BaseMetadata): CreateEventInput {
	return {
		category: AnalyticsCategory.DATA_COLLECTION,
		action,
		metadata,
	};
}

// ============================================================================
// USAGE EXAMPLES
// ============================================================================

/* 
// Type-safe event creation examples:

// 1. Using helper functions (recommended)
const navEvent = createNavigationEvent(NavigationAction.PAGE_VIEW, {
  route: '/projects',
  loadTime: 1250
});

// 2. Direct creation with full autocomplete
const contentEvent: CreateEventInput = {
  category: AnalyticsCategory.CONTENT,
  action: ContentAction.CREATE,
  metadata: {
    contentType: 'project',
    templateId: 'survey_basic',
    fieldCount: 8
  }
};

// 3. Custom values when needed (escape hatch)
const customEvent: CreateEventInput = {
  category: 'custom_feature' as EventCategory,
  action: 'custom_action' as EventAction, 
  metadata: { customField: 'value' }
};

// 4. Validation before sending
const validation = validateEvent(customEvent);
if (validation.warnings.length > 0) {
  console.warn('Event validation warnings:', validation.warnings);
}
if (!validation.isValid) {
  console.error('Event validation errors:', validation.errors);
}
*/
